import { useRef, useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { RotateCcw, Copy, Download, Check, Smartphone, Tablet, Pencil, PencilOff } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/hooks/use-i18n";
import { captureEvent } from "@/lib/posthog";

interface MobilePreviewProps {
  html: string;
  isLoading?: boolean;
  onRefresh?: () => void;
  onHtmlChange?: (html: string) => void;
}

type DeviceType = "iphone" | "android";

export function MobilePreview({ html, isLoading, onRefresh, onHtmlChange }: MobilePreviewProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [copied, setCopied] = useState(false);
  const [deviceType, setDeviceType] = useState<DeviceType>("iphone");
  const [isEditMode, setIsEditMode] = useState(false);
  const [scale, setScale] = useState(1);
  const { toast } = useToast();
  const { t } = useI18n();

  useEffect(() => {
    const updateScale = () => {
      const width = window.innerWidth;
      if (width < 640) setScale(0.5);
      else if (width < 768) setScale(0.65);
      else if (width < 1024) setScale(0.8);
      else setScale(1);
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  const injectEditMode = useCallback((doc: Document, enabled: boolean, indicatorText?: string) => {
    const existingStyle = doc.getElementById("edit-mode-styles");
    const existingScript = doc.getElementById("edit-mode-script");
    
    if (existingStyle) existingStyle.remove();
    if (existingScript) existingScript.remove();

    if (enabled) {
      const style = doc.createElement("style");
      style.id = "edit-mode-styles";
      style.textContent = `
        [data-editable="true"] {
          cursor: text !important;
          outline: 2px dashed transparent !important;
          outline-offset: 2px !important;
          transition: outline-color 0.2s, background-color 0.2s !important;
          border-radius: 4px !important;
        }
        [data-editable="true"]:hover {
          outline-color: #F5B800 !important;
          background-color: rgba(245, 184, 0, 0.1) !important;
        }
        [data-editable="true"]:focus {
          outline-color: #F5B800 !important;
          outline-style: solid !important;
          background-color: rgba(245, 184, 0, 0.15) !important;
        }
        .edit-mode-indicator {
          position: fixed;
          top: 8px;
          right: 8px;
          background: #F5B800;
          color: #000;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 600;
          z-index: 10000;
          font-family: sans-serif;
        }
      `;
      doc.head.appendChild(style);

      const indicator = doc.createElement("div");
      indicator.className = "edit-mode-indicator";
      indicator.textContent = indicatorText || "EDIT MODE";
      doc.body.appendChild(indicator);

      const editableSelectors = "h1, h2, h3, h4, h5, h6, p, li, span, td, th, label, a, strong, em, b, i";
      const elements = doc.querySelectorAll(editableSelectors);
      elements.forEach((el) => {
        const htmlEl = el as HTMLElement;
        if (htmlEl.childNodes.length === 0) return;
        const hasDirectText = Array.from(htmlEl.childNodes).some(
          node => node.nodeType === Node.TEXT_NODE && node.textContent?.trim()
        );
        if (hasDirectText || htmlEl.children.length === 0) {
          htmlEl.setAttribute("contenteditable", "true");
          htmlEl.setAttribute("data-editable", "true");
        }
      });
    } else {
      const editables = doc.querySelectorAll('[data-editable="true"]');
      editables.forEach((el) => {
        el.removeAttribute("contenteditable");
        el.removeAttribute("data-editable");
      });
      const indicator = doc.querySelector(".edit-mode-indicator");
      if (indicator) indicator.remove();
    }
  }, []);

  const extractHtmlFromIframe = useCallback(() => {
    if (!iframeRef.current?.contentDocument) return null;
    const doc = iframeRef.current.contentDocument;
    
    const indicator = doc.querySelector(".edit-mode-indicator");
    const editStyle = doc.getElementById("edit-mode-styles");
    if (indicator) indicator.remove();
    if (editStyle) editStyle.remove();
    
    const editables = doc.querySelectorAll('[data-editable="true"]');
    editables.forEach((el) => {
      el.removeAttribute("contenteditable");
      el.removeAttribute("data-editable");
    });
    
    return "<!DOCTYPE html>\n" + doc.documentElement.outerHTML;
  }, []);

  const toggleEditMode = useCallback(() => {
    if (isEditMode && onHtmlChange) {
      const updatedHtml = extractHtmlFromIframe();
      if (updatedHtml) {
        if (iframeRef.current?.contentDocument) {
          injectEditMode(iframeRef.current.contentDocument, false, t("editModeIndicator"));
        }
        onHtmlChange(updatedHtml);
        setIsEditMode(false);
        captureEvent("html_edit_saved");
        toast({
          title: t("changesSaved"),
          description: t("editsApplied"),
        });
        return;
      }
    }
    
    const newEditMode = !isEditMode;
    setIsEditMode(newEditMode);
    captureEvent("edit_mode_toggled", { enabled: newEditMode });
    
    if (iframeRef.current?.contentDocument) {
      injectEditMode(iframeRef.current.contentDocument, newEditMode, t("editModeIndicator"));
    }
    
    if (newEditMode) {
      toast({
        title: t("editModeEnabled"),
        description: t("clickToEdit"),
      });
    }
  }, [isEditMode, onHtmlChange, extractHtmlFromIframe, injectEditMode, toast, t]);

  const lastWrittenHtmlRef = useRef<string>("");
  
  useEffect(() => {
    if (iframeRef.current && html) {
      if (html === lastWrittenHtmlRef.current) {
        return;
      }
      
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(html);
        doc.close();
        lastWrittenHtmlRef.current = html;
        
        if (isEditMode) {
          setTimeout(() => injectEditMode(doc, true, t("editModeIndicator")), 50);
        }
      }
    }
  }, [html, isEditMode, injectEditMode, t]);

  const copyToClipboard = async (text: string): Promise<boolean> => {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {}
    }
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.top = "-9999px";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      const success = document.execCommand("copy");
      document.body.removeChild(textarea);
      return success;
    } catch {
      document.body.removeChild(textarea);
      return false;
    }
  };

  const handleCopyHtml = async () => {
    const success = await copyToClipboard(html);
    if (success) {
      setCopied(true);
      captureEvent("html_copied", { html_length: html.length });
      toast({
        title: t("copiedToClipboard"),
        description: t("htmlCopied"),
      });
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast({
        title: t("failedToCopy"),
        description: t("tryAgainOrDownload"),
        variant: "destructive",
      });
    }
  };

  const handleDownload = () => {
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "awards-page.html";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    captureEvent("html_downloaded", { html_length: html.length });
    toast({
      title: t("downloaded"),
      description: t("htmlDownloaded"),
    });
  };

  const deviceStyles = {
    iphone: {
      width: 375,
      height: 667,
      borderRadius: "3rem",
      notchWidth: 150,
      notchHeight: 30,
    },
    android: {
      width: 360,
      height: 640,
      borderRadius: "2rem",
      notchWidth: 100,
      notchHeight: 24,
    },
  };

  const device = deviceStyles[deviceType];

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between p-2 sm:p-3 border-b bg-card gap-2 flex-wrap shrink-0">
        <div className="flex items-center gap-1 sm:gap-2">
          <Button
            size="sm"
            variant={deviceType === "iphone" ? "default" : "ghost"}
            onClick={() => {
              captureEvent("device_preview_changed", { device: "iphone" });
              setDeviceType("iphone");
            }}
            className="px-2 sm:px-3"
            data-testid="button-device-iphone"
          >
            <Smartphone className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">{t("iPhone")}</span>
          </Button>
          <Button
            size="sm"
            variant={deviceType === "android" ? "default" : "ghost"}
            onClick={() => {
              captureEvent("device_preview_changed", { device: "android" });
              setDeviceType("android");
            }}
            className="px-2 sm:px-3"
            data-testid="button-device-android"
          >
            <Tablet className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">{t("android")}</span>
          </Button>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          {onRefresh && (
            <Button
              size="icon"
              variant="ghost"
              onClick={onRefresh}
              disabled={isLoading}
              data-testid="button-refresh-preview"
            >
              <RotateCcw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
            </Button>
          )}
          <Button
            size="sm"
            variant={isEditMode ? "default" : "outline"}
            onClick={toggleEditMode}
            disabled={!html || isLoading}
            className="px-2 sm:px-3"
            data-testid="button-toggle-edit"
          >
            {isEditMode ? (
              <>
                <PencilOff className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">{t("doneEditing")}</span>
              </>
            ) : (
              <>
                <Pencil className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">{t("edit")}</span>
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleCopyHtml}
            disabled={!html}
            className="px-2 sm:px-3"
            data-testid="button-copy-html"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">{t("copied")}</span>
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">{t("copyHTML")}</span>
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleDownload}
            disabled={!html}
            className="hidden sm:flex"
            data-testid="button-download-html"
          >
            <Download className="h-4 w-4 mr-1" />
            {t("download")}
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex items-start lg:items-center justify-center p-2 sm:p-6 bg-gradient-to-br from-muted/30 to-muted/60 overflow-auto">
        <div 
          className="shrink-0"
          style={{
            width: (device.width + 24) * scale,
            height: (device.height + 48) * scale,
          }}
        >
          <div
            className="relative bg-black shadow-2xl origin-top-left"
            style={{
              width: device.width + 24,
              height: device.height + 48,
              borderRadius: device.borderRadius,
              padding: "12px",
              transform: `scale(${scale})`,
            }}
          >
          <div
            className="absolute left-1/2 transform -translate-x-1/2 bg-black z-10"
            style={{
              top: "8px",
              width: device.notchWidth,
              height: device.notchHeight,
              borderRadius: "0 0 1rem 1rem",
            }}
          />

          <div
            className="relative bg-white overflow-hidden"
            style={{
              width: device.width,
              height: device.height,
              borderRadius: `calc(${device.borderRadius} - 8px)`,
            }}
          >
            {isLoading ? (
              <div className="absolute inset-0 flex items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-4">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg
                        className="w-8 h-8 text-primary animate-pulse"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">{t("generatingAwardsPage")}</p>
                </div>
              </div>
            ) : html ? (
              <iframe
                ref={iframeRef}
                className="w-full h-full border-0"
                title="Mobile Preview"
                sandbox="allow-same-origin"
                data-testid="iframe-preview"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-muted/50">
                <div className="text-center p-6">
                  <svg
                    className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                    />
                  </svg>
                  <p className="text-sm text-muted-foreground">
                    {t("describeAwardsToPreview")}
                  </p>
                </div>
              </div>
            )}

            {html && !isLoading && (
              <div className="absolute bottom-2 right-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              </div>
            )}
          </div>

          <div
            className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-gray-700 rounded-full"
            style={{ width: 100, height: 4 }}
          />
        </div>
        </div>
      </div>
    </div>
  );
}
